import React from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useExperiment } from '../context/AnalyticsContext';

interface Props {
  featureFlag: string;
  defaultState: 'visible' | 'hidden';
  alternateComponent?: React.ReactNode;
  children?: React.ReactNode;
}

type FeatureFlagBooleanInput = boolean | string | null | undefined;

const normalizeFeatureFlagBooleanValue = (value: FeatureFlagBooleanInput): boolean | null => {
  if (value === true || value === 'true' || value === 'on') {
    return true;
  }

  if (value === false || value === 'false' || value === 'off') {
    return false;
  }

  return null;
};

const getAmplitudeBooleanValue = (
  payload: FeatureFlagBooleanInput,
  value: FeatureFlagBooleanInput
): boolean | null => {
  const normalizedPayload = normalizeFeatureFlagBooleanValue(payload);

  if (normalizedPayload !== null) {
    return normalizedPayload;
  }

  return normalizeFeatureFlagBooleanValue(value);
};

const getRenderContent = ({
  value,
  defaultState,
  alternateComponent,
  children,
}: {
  value: boolean | null;
  defaultState: Props['defaultState'];
  alternateComponent?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  if (defaultState === 'hidden') {
    return value === true ? children : alternateComponent || null;
  }

  return value === true ? alternateComponent || null : children;
};

const FeatureFlagBoolean = ({ featureFlag, defaultState, alternateComponent, children }: Props) => {
  const gbFeatureValue = useFeatureValue(featureFlag, null);
  const normalizedGbFeatureValue = normalizeFeatureFlagBooleanValue(gbFeatureValue);

  const experiment = useExperiment();
  const formattedFeatureFlag = featureFlag.split(':').join('-');
  const ampFeatureValue = experiment.variant(formattedFeatureFlag);
  let value =
    normalizedGbFeatureValue !== null
      ? normalizedGbFeatureValue
      : getAmplitudeBooleanValue(ampFeatureValue?.payload, ampFeatureValue?.value);

  const overrideFeatureFlagValue =
    typeof window !== 'undefined'
      ? new URLSearchParams(window?.location?.search)?.get(featureFlag)
      : null;

  if (overrideFeatureFlagValue !== null) {
    value = normalizeFeatureFlagBooleanValue(overrideFeatureFlagValue);
  }

  return (
    <>
      {getRenderContent({
        value,
        defaultState,
        alternateComponent,
        children,
      })}
    </>
  );
};

export default FeatureFlagBoolean;
